import LeftSideAttachedToComponent from "../components/LeftSideAttachedToComponent";
import RightSideAttachedToComponent from "../components/RightSideAttachedToComponent";
import { LEFT, RIGHT } from "../constants";
import { COMPONENT_IDS } from "../storage2ComponentIDs";
import { entitiesAreEqualDepth } from "./sideUnitReplacement";
import { getAdjustableShelfAssemblyPart, isAdjustableEntity, } from "../utils/entityUtils";
export const bothEntitySidesAreAttached = (entity) => entity.hasComponent(LeftSideAttachedToComponent.id) &&
    entity.hasComponent(RightSideAttachedToComponent.id);
export const entityIsSection = (entity) => entity.hasComponent(COMPONENT_IDS.SECTION_COMPONENT);
const getEntityDataObject = (entity, connector) => ({
    entity,
    connector,
});
export const entityIsAttachedAtSide = (entity, side) => {
    const directionComponent = getAttachmentComponentBySide(side);
    return entity.hasComponent(directionComponent);
};
export const getOppositeSide = (side) => side === LEFT ? RIGHT : LEFT;
export const getAttachmentComponentBySide = (side) => side === LEFT
    ? COMPONENT_IDS.LEFT_SIDE_ATTACHED_TO
    : COMPONENT_IDS.RIGHT_SIDE_ATTACHED_TO;
export const getRemovedSideUnitComponentBasedOnSide = (side) => side === LEFT
    ? COMPONENT_IDS.REMOVE_LEFT_SIDE_UNIT_ID
    : COMPONENT_IDS.REMOVE_RIGHT_SIDE_UNIT_ID;
/**
 * Collects attachments of an entity based on leftSideAttachedToComponent and RightSideAttachedToComponent.
 *
 * @param entity
 * @param attachments
 * @param direction - Direction is either LEFT or RIGHT and determines which direction
 *                    one would like to collect attachments. If an option is not supplied,
 *                    it is set to it's default null value, it will collect all attachments,
 *                    eg. starting with supplied entity and work in both directions.
 * @param index -     Index can be either true or a number. If true, it will collect all attachments. If number, it will
 *                    collect said number.
 */
export const getAttachedToRecursively = ({ entity, attachments = [], direction = null, index = true, }) => {
    const getReturnValue = (e) => {
        if (!e)
            return attachments;
        return direction === LEFT ? [e, ...attachments] : [...attachments, e];
    };
    if (!direction)
        return [
            ...getAttachedToRecursively({ entity, direction: LEFT }),
            entity,
            ...getAttachedToRecursively({ entity, direction: RIGHT }),
        ];
    const pathWay = direction === RIGHT;
    const directionComponent = getAttachmentComponentBySide(direction);
    const keepGoing = index &&
        entityIsAttachedAtSide(entity, direction) &&
        entitiesAreEqualDepth(entity, pathWay ? LEFT : RIGHT);
    if (!keepGoing)
        return getReturnValue();
    const attachedTo = entity.getComponent(directionComponent).attachedTo;
    const collectedAttachments = getReturnValue(attachedTo);
    const i = index === true ? index : index - 1;
    return getAttachedToRecursively({
        entity: attachedTo,
        attachments: collectedAttachments,
        direction,
        index: i,
    });
};
export const getFirstAttachmentBasedOnEntity = (entity) => {
    const stayOnCurrentEntity = !entityIsAttachedAtSide(entity, LEFT) &&
        !entitiesAreEqualDepth(entity, RIGHT);
    if (stayOnCurrentEntity)
        return entity;
    const [firstEntity] = getAttachedToRecursively({ entity, direction: LEFT });
    return firstEntity;
};
export const addAttachedToRelations = (leftEntity, rightEntity) => {
    if (isAdjustableEntity(leftEntity) && isAdjustableEntity(rightEntity)) {
        const rightEntityLeftPart = getAdjustableShelfAssemblyPart(rightEntity, "left-shelf");
        const leftEntityRightPart = getAdjustableShelfAssemblyPart(leftEntity, "right-shelf");
        addAttachmentComponents(rightEntityLeftPart, leftEntityRightPart);
    }
    else if (isAdjustableEntity(rightEntity)) {
        const rightEntityLeftPart = getAdjustableShelfAssemblyPart(rightEntity, "left-shelf");
        addAttachmentComponents(rightEntityLeftPart, leftEntity);
    }
    else if (isAdjustableEntity(leftEntity)) {
        const leftEntityRightPart = getAdjustableShelfAssemblyPart(leftEntity, "right-shelf");
        addAttachmentComponents(rightEntity, leftEntityRightPart);
    }
    else {
        addAttachmentComponents(rightEntity, leftEntity);
    }
};
export const addAttachedToRelationsBasedOnAlignments = (alignmentConnectionComponent) => {
    if (!alignmentConnectionComponent.connections.length)
        return;
    alignmentConnectionComponent.connections.forEach((connection) => {
        const { left, right } = getEntitiesBySideBasedOnAlignments(connection);
        addAttachedToRelations(left, right);
    });
};
export const getEntitiesBySideBasedOnAlignments = (alignmentConnection) => {
    const { demandingEntity, offeringEntity, demandingConnector, offeringConnector, } = alignmentConnection;
    return sortEntitiesBySideBasedOnAlignment(getEntityDataObject(demandingEntity, demandingConnector), getEntityDataObject(offeringEntity, offeringConnector));
};
export const getEntityAttachmentComponents = (_entity) => ({
    left: _entity.getComponent(LeftSideAttachedToComponent.id),
    right: _entity.getComponent(RightSideAttachedToComponent.id),
});
export const getEntityAttachments = (_entity) => {
    const { left, right } = getEntityAttachmentComponents(_entity);
    return {
        left: left && left.attachedTo,
        right: right && right.attachedTo,
    };
};
export const sortEntitiesBySideBasedOnAlignment = (entityDataA, entityDataB) => ({
    left: getEntityByDesiredSide(entityDataA, entityDataB, LEFT).entity,
    right: getEntityByDesiredSide(entityDataA, entityDataB, RIGHT).entity,
});
export const getAttachedToComponent = (_entity) => {
    var _a, _b;
    return (((_a = _entity.getComponent(LeftSideAttachedToComponent.id)) === null || _a === void 0 ? void 0 : _a.attachedTo) ||
        ((_b = _entity.getComponent(RightSideAttachedToComponent.id)) === null || _b === void 0 ? void 0 : _b.attachedTo));
};
const getEntityByDesiredSide = (entityDataA, entityDataB, targetSide) => {
    const { left, right } = entityDataA.connector.key.includes("left")
        ? { left: entityDataA, right: entityDataB }
        : { left: entityDataB, right: entityDataA };
    return targetSide === LEFT ? right : left;
};
export const removeEntityAttachmentBySide = (_entity, _side) => {
    const directionComponent = getAttachmentComponentBySide(_side);
    if (isAdjustableEntity(_entity)) {
        return getAdjustableShelfAssemblyPart(_entity, `${_side}-shelf`).removeComponent(directionComponent);
    }
    return _entity.removeComponent(directionComponent);
};
export const entitiesAreAttached = (leftEntity, rightEntity) => {
    var _a, _b;
    const leftEntityAttachedToId = (_a = leftEntity.getComponent(COMPONENT_IDS.RIGHT_SIDE_ATTACHED_TO)) === null || _a === void 0 ? void 0 : _a.attachedTo.id;
    const rightEntityAttachedToId = (_b = rightEntity.getComponent(COMPONENT_IDS.LEFT_SIDE_ATTACHED_TO)) === null || _b === void 0 ? void 0 : _b.attachedTo.id;
    return (leftEntity.id === rightEntityAttachedToId &&
        rightEntity.id === leftEntityAttachedToId);
};
export const verifyAttachments = (attachments, index = 0) => {
    const keepGoing = (res) => res ? verifyAttachments(attachments, index + 1) : false;
    const currentEntity = attachments[index];
    const hasOnlyOneEntity = attachments.length === 1;
    const handlingFirstEntity = index === 0;
    const handlingLastEntity = index + 1 === attachments.length;
    if (hasOnlyOneEntity) {
        return (!entityIsAttachedAtSide(currentEntity, LEFT) &&
            !entityIsAttachedAtSide(currentEntity, RIGHT));
    }
    if (handlingFirstEntity) {
        const hasAttachmentToRightSideOnly = !entityIsAttachedAtSide(currentEntity, LEFT) &&
            entitiesAreAttached(currentEntity, attachments[index + 1]);
        return keepGoing(hasAttachmentToRightSideOnly);
    }
    if (handlingLastEntity) {
        return !entityIsAttachedAtSide(currentEntity, RIGHT);
    }
    const hasAttachmentOnBothSides = entitiesAreAttached(currentEntity, attachments[index + 1]);
    return keepGoing(hasAttachmentOnBothSides);
};
const addAttachmentComponents = (rightEntity, leftEntity) => {
    const attachedToRightComponent = new RightSideAttachedToComponent(rightEntity);
    const attachedToLeftComponent = new LeftSideAttachedToComponent(leftEntity);
    rightEntity.addComponent(attachedToLeftComponent);
    leftEntity.addComponent(attachedToRightComponent);
};
